import * as React from "react";
import Map, {
  GeolocateControl,
  Layer,
  Marker,
  NavigationControl,
  Source,
} from "react-map-gl";
import maplibregl from "maplibre-gl";
import { feature } from "topojson-client";
import SearchBar from "./SearchBar";
import { Legend } from "./Legend";
import { CircularProgress } from "@mui/material";
import { Attribution } from "./Attribution";
import classnames from "classnames";
import { Overlay } from "./Overlay";
import { logAmplitudeEventWithData } from "./Amplitude";
import { use100vh } from "react-div-100vh";
import {
  getClickStyle,
  getHoverStyle,
  getLineStyle,
  getPointStyle,
  MAPBOX_STYLE_URL,
  MAPBOX_TOKEN,
  transformStyleRequest,
} from "./MapStyles";
import { useSearchParams } from "react-router-dom";

import "maplibre-gl/dist/maplibre-gl.css";

export const OutboundLink = ({ to, children, className, referrer }) => (
  <a
    className={className}
    href={to}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() =>
      logAmplitudeEventWithData("clickOutboundLink", {
        url: to,
        referrer: referrer || "none",
      })
    }
  >
    {children}
  </a>
);

const BLANK_BBL = "0000000000";

const BuildingsMap = () => {
  const [mapData, setMapData] = React.useState(null);
  const [ntaData, setNtaData] = React.useState(null);
  const [isInteractingWithMap, setInteractingWithMap] = React.useState(false);
  const [hoverPoint, setHoverPoint] = React.useState(BLANK_BBL);
  const [selectedBuilding, setSelectedBuilding] = React.useState(null);
  const [marker, setMarker] = React.useState([]);
  const [referrer, setReferrer] = React.useState("none");

  const [searchParams, setSearchParams] = useSearchParams();

  const mapRef = React.useRef();

  const zoomToPoint = (coords) => {
    if (coords.length === 2) {
      mapRef.current.fitBounds([coords, coords], {
        padding: 1000,
        duration: 4000,
      });
    }
  };

  const onSearchResult = (coords, address, bbl) => {
    const buildingData = mapData.features.find(
      (feature) => feature.properties.bbl.toString() === bbl
    );
    if (buildingData && buildingData.geometry && buildingData.properties) {
      setSearchParams({ bbl: bbl });
      setSelectedBuilding(buildingData.properties);
      setMarker([]);
      const { coordinates } = buildingData.geometry;
      zoomToPoint(coordinates);
      logAmplitudeEventWithData("searchAddress", {
        referrer: referrer,
        ...buildingData.properties,
      });
    } else if (!!coords) {
      setSearchParams({});
      setSelectedBuilding({ a: address });
      setMarker(coords);
      zoomToPoint(coords);
      logAmplitudeEventWithData("searchAddress", {
        a: address,
        referrer: referrer,
      });
    }
  };

  React.useEffect(() => {
    fetch("./data/buildings.json", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((json) => {
        setMapData(feature(json, json.objects.buildings));
        console.log("Buildings data loaded");
      })
      .catch((err) => console.error("Could not load map data", err));

    fetch("./data/ntas.json", {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((resp) => resp.json())
      .then((json) => {
        setNtaData(feature(json, json.objects.ntas));
        console.log("Districts data loaded");
      });
    const referralMedium = searchParams.get("nyupanel");
    if (referralMedium) {
      setReferrer(referralMedium);
      logAmplitudeEventWithData("flyerCampaignReferral", {
        referrer: referralMedium,
      });
    }
  }, []); // eslint-disable-line

  React.useEffect(() => {
    const bbl = searchParams.get("bbl");
    if (!!mapData && bbl && bbl.length === 10) {
      const buildingData = mapData.features.find(
        (feature) => feature.properties.bbl.toString() === bbl
      );
      if (buildingData && buildingData.properties) {
        setInteractingWithMap(true);
        setSearchParams({ bbl: bbl });
        setSelectedBuilding(buildingData.properties);
        setMarker([]);
        logAmplitudeEventWithData("addressInUrl", {
          referrer: referrer,
          ...buildingData.properties,
        });
      }
    }
  }, [mapData]); // eslint-disable-line

  const onHover = React.useCallback((event) => {
    const building = event.features && event.features[0];
    setHoverPoint(
      (!!building && building.properties && building.properties.bbl) ||
        BLANK_BBL
    );
  }, []);

  const onClick = React.useCallback((event) => {
    setInteractingWithMap(true);
    const building = event.features && event.features[0];
    if (!!building && building.properties) {
      setSelectedBuilding(building.properties);
      setSearchParams({ bbl: building.properties.bbl });
      logAmplitudeEventWithData("clickOnPoint", {
        referrer: referrer,
        ...building.properties,
      });
    } else {
      setSelectedBuilding(null);
      setSearchParams({});
    }
  }, []); // eslint-disable-line

  const hoverFilter = React.useMemo(
    () => ["in", "bbl", hoverPoint],
    [hoverPoint]
  );

  const clickFilter = React.useMemo(
    () => [
      "in",
      "bbl",
      selectedBuilding && selectedBuilding.bbl
        ? selectedBuilding.bbl
        : BLANK_BBL,
    ],
    [selectedBuilding]
  );

  const onMouseLeave = React.useCallback(() => setHoverPoint(BLANK_BBL), []);

  const viewportHeight = use100vh();

  return (
    <div className={classnames("app", isInteractingWithMap && "is-active")}>
      <Overlay selectedBuilding={selectedBuilding} referrer={referrer} />
      {!!mapData ? (
        <Map
          ref={mapRef}
          mapLib={maplibregl}
          initialViewState={{
            longitude: -73.977708344928,
            latitude: 40.713323256573386,
            zoom: 10,
          }}
          style={{
            width: "100%",
            height: viewportHeight,
            // This fixes an issue where Chrome/Safari bottom nav overlaps with map content on iPhones.
            // See https://allthingssmitty.com/2020/05/11/css-fix-for-100vh-in-mobile-webkit/
            maxHeight: "-webkit-fill-available",
          }}
          mapStyle={MAPBOX_STYLE_URL}
          transformRequest={transformStyleRequest}
          onMouseMove={onHover}
          onClick={onClick}
          scrollZoom={false}
          dragRotate={false}
          onMouseLeave={onMouseLeave}
          interactiveLayerIds={["buildings"]}
          cursor="pointer"
          minZoom={9}
          maxZoom={18}
          onZoom={() => setInteractingWithMap(true)}
          mapboxAccessToken={MAPBOX_TOKEN}
          attributionControl={false}
        >
          {!!ntaData && (
            <Source id="ntas-boundaries" type="geojson" data={ntaData}>
              <Layer {...getLineStyle()} />
            </Source>
          )}

          <Source id="buildings-data" type="geojson" data={mapData}>
            <Layer {...getPointStyle()} />
            <Layer {...getHoverStyle()} filter={hoverFilter} />
            <Layer {...getClickStyle()} filter={clickFilter} />

            {!!marker && marker.length === 2 && (
              <Marker
                longitude={marker[0]}
                latitude={marker[1]}
                color="#8c8c8c"
              />
            )}
          </Source>

          <GeolocateControl onGeolocate={() => setInteractingWithMap(true)} />
          <NavigationControl showCompass={false} />
          <SearchBar
            onSearchResult={onSearchResult}
            mapboxAccessToken={MAPBOX_TOKEN}
            onClear={() => {
              setMarker([]);
            }}
            position="top-left"
          />
          <Attribution />

          <Legend is2018Map={false} />
        </Map>
      ) : (
        <div className="loading-screen">
          <div>
            <CircularProgress color="inherit" />
            <p>Loading...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildingsMap;
